import axios from 'axios';
import apiClient from '../api/apiClient';
import {
  CreateManagedUserInput,
  EmployeeDirectoryEmployee,
  EmployeeReconciliationRun,
  ManagedRoleAssignmentInput,
  ManagedUser,
  ManagedUsersPage,
  ManagedUsersQuery,
  UserManagementMetadata,
  UserManagementRoleMatrix,
} from '../Types/UserManagement';

interface ApiEnvelope<T> {
  data: T;
}

const unwrap = <T,>(response: { data: ApiEnvelope<T> }): T => response.data.data;

export const getUserManagementMetadata = async (): Promise<UserManagementMetadata> =>
  unwrap(await apiClient.get<ApiEnvelope<UserManagementMetadata>>('/api/user-management/metadata'));

export const getManagedUsers = async (query: ManagedUsersQuery): Promise<ManagedUsersPage> => {
  const params = new URLSearchParams();
  if (query.search?.trim()) params.set('search', query.search.trim());
  if (query.roleId) params.set('roleId', query.roleId);
  if (query.locationId) params.set('locationId', query.locationId);
  params.set('isActive', 'true');
  params.set('pageNumber', String(query.pageNumber));
  params.set('pageSize', String(query.pageSize));
  return unwrap(
    await apiClient.get<ApiEnvelope<ManagedUsersPage>>(
      `/api/user-management/users?${params.toString()}`,
    ),
  );
};

export const getManagedUser = async (appUserId: string): Promise<ManagedUser> =>
  unwrap(
    await apiClient.get<ApiEnvelope<ManagedUser>>(
      `/api/user-management/users/${encodeURIComponent(appUserId)}`,
    ),
  );

export const createManagedUser = async (
  input: CreateManagedUserInput,
): Promise<ManagedUser> =>
  unwrap(
    await apiClient.post<ApiEnvelope<ManagedUser>>('/api/user-management/users', input),
  );

export const searchEmployeeDirectory = async (
  search: string,
  options?: {
    limit?: number;
    includeProvisioned?: boolean;
  },
): Promise<EmployeeDirectoryEmployee[]> => {
  const params = new URLSearchParams({
    search: search.trim(),
    limit: String(options?.limit ?? 20),
    includeProvisioned: String(options?.includeProvisioned ?? false),
    includeTerminated: 'false',
  });
  return unwrap(
    await apiClient.get<ApiEnvelope<EmployeeDirectoryEmployee[]>>(
      `/api/user-management/employee-directory?${params.toString()}`,
    ),
  );
};

export const getLatestEmployeeReconciliation = async (
): Promise<EmployeeReconciliationRun | null> =>
  unwrap(
    await apiClient.get<ApiEnvelope<EmployeeReconciliationRun | null>>(
      '/api/user-management/employee-reconciliation/latest',
    ),
  );

export const replaceManagedUserRoles = async (
  appUserId: string,
  roleAssignments: ManagedRoleAssignmentInput[],
): Promise<ManagedUser> =>
  unwrap(
    await apiClient.put<ApiEnvelope<ManagedUser>>(
      `/api/user-management/users/${encodeURIComponent(appUserId)}/roles`,
      { roleAssignments },
    ),
  );

export const updateManagedUserStatus = async (
  appUserId: string,
  isActive: boolean,
): Promise<ManagedUser> =>
  unwrap(
    await apiClient.patch<ApiEnvelope<ManagedUser>>(
      `/api/user-management/users/${encodeURIComponent(appUserId)}/status`,
      { isActive },
    ),
  );

export const getUserManagementRoleMatrix = async (): Promise<UserManagementRoleMatrix[]> =>
  unwrap(
    await apiClient.get<ApiEnvelope<UserManagementRoleMatrix[]>>(
      '/api/user-management/role-matrix',
    ),
  );

export const getUserManagementErrorMessage = (
  error: unknown,
  fallback: string,
): string => {
  if (!axios.isAxiosError(error)) return fallback;
  const message = error.response?.data?.error?.message;
  return typeof message === 'string' && message.trim() ? message : fallback;
};
